<template>
  <FormWrapper
    class="column"
    @submit="search"
  >
    <InputSelect
      v-model="stockExchange"
      :disabled="disabled"
      :label="$t('select_stock_symbol.select_stock_exchange_label')"
      new-design
      option-label="label"
      option-prop="extension"
      :options="exchangeOptions"
    />

    <InputText
      ref="query"
      v-model="query"
      auto-focus
      class="q-mt-lg"
      clearable
      :label="$t('select_stock_symbol.stock_symbol_label')"
      new-design
      :placeholder="$t('select_stock_symbol.stock_symbol_placeholder')"
    >
      <template #append>
        <BaseButton
          :aria-label="$t('select_stock_symbol.search_add_button')"
          color="secondary"
          :disabled="disabled"
          :label="buttonText"
          new-design
          type="submit"
        />
      </template>
    </InputText>

    <div class="row q-mt-md justify-center">
      <QSpinner
        v-if="loading"
        :size="16"
      />
      <div v-else-if="searchResult">
        <QIcon name="done" /> {{ searchResult }}
      </div>
      <div v-else-if="notFound"><QIcon name="clear" /> {{ notFound }}</div>
    </div>
  </FormWrapper>
</template>

<script>
import { storeToRefs } from "pinia";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  FormWrapper,
  InputSelect,
  InputText,
} from "shared/components/base";
import { stockAliases } from "shared/constants";
import { cleanStockSymbol } from "shared/helpers/string";
import { useGlobalStore } from "shared/stores/global";

export default {
  name: "SelectStockSymbol",
  components: {
    FormWrapper,
    BaseButton,
    InputSelect,
    InputText,
  },
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    buttonText: {
      type: String,
      default: () => getLocaleText("select_stock_symbol.button_text"),
    },
    disabled: {
      type: Boolean,
    },
  },
  emits: ["update:modelValue"],
  setup() {
    const globalStore = useGlobalStore();
    const { appConfig } = storeToRefs(globalStore);

    return {
      appConfig,
    };
  },
  data() {
    return {
      isFocused: false,
      query: this.modelValue,
      loading: false,
      searchResult: "",
      notFound: "",
      stockExchange: null,
      exchangeOptions: [
        {
          label: this.$t("select_stock_symbol.asx_label"),
          extension: ".AX",
          defaultFor: "Streem",
        },
        {
          label: this.$t("select_stock_symbol.lse_label"),
          extension: ".L",
          defaultFor: "CisionOne",
        },
        { label: this.$t("select_stock_symbol.nzx_label"), extension: ".NZ" },
        { label: this.$t("select_stock_symbol.nyse_label"), extension: "" },
        { label: this.$t("select_stock_symbol.par_label"), extension: ".PA" },
      ],
    };
  },
  computed: {
    searchStock: {
      get() {
        return this.modelValue;
      },
      set(stock) {
        this.$emit("update:modelValue", stock);
        this.isFocused = false;
        this.$refs.query.blur();
      },
    },
    lookupAliases() {
      return Object.entries(stockAliases).reduce(
        (reversed, [key, value]) => Object.assign(reversed, { [value]: key }),
        {}
      );
    },
  },
  watch: {
    query: {
      deep: true,
      handler(newValue) {
        if (newValue === "") {
          this.$emit("update:modelValue", newValue);
        }
      },
    },
  },
  mounted() {
    this.setDefaultExchange();

    this.$watch(
      () => this.$refs.query.focused,
      (value) => {
        this.isFocused = Boolean(value);
      }
    );
  },
  methods: {
    /** @public */
    clearQuery() {
      this.query = "";
    },
    setDefaultExchange() {
      const matches = this.searchStock && this.searchStock.match(/\.\w+$/);

      if (matches) {
        this.stockExchange = this.exchangeOptions.find(
          (option) => option.extension === matches[0]
        );

        this.query = cleanStockSymbol(this.searchStock);
      } else {
        this.stockExchange = this.exchangeOptions.find(
          (option) => option.defaultFor === this.appConfig.platformName
        );
      }
    },
    applyQueryAlias(query) {
      const regexString = Object.keys(this.lookupAliases)
        .map((alias) => alias.replace("^", "\\^"))
        .join("|");

      const aliasRegex = new RegExp(`^(${regexString})$`);

      return query.replace(
        aliasRegex,
        (_match, group) => this.lookupAliases[group]
      );
    },
    async search() {
      try {
        this.loading = true;
        this.searchResult = "";
        this.notFound = "";

        let { query } = this;
        const aliasedQuery = this.applyQueryAlias(query);

        if (aliasedQuery !== query) {
          query = aliasedQuery;
        } else if (/^[^^]/.test(query)) {
          query += this.stockExchange.extension;
        }

        const params = { query };

        const result = (
          await this.$streemApiV1.get("search/stock_symbol", { params })
        ).data;

        if (result.length) {
          const name = result[0].longname || result[0].shortname;
          this.searchResult = `${name} (${cleanStockSymbol(result[0].symbol)})`;
          this.searchStock = result[0].symbol;
        } else {
          this.notFound = this.$t("select_stock_symbol.search_empty", {
            query: this.query,
          });
        }
      } catch {
        this.$q.notify(this.$t("select_stock_symbol.search_error"));
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.base-button) {
  border-radius: 3px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
